/**
 * Vendor service-area contract (GG-169) — the geography gate's config
 * shape and the admin endpoints that read/update it.
 *
 * The area list lives in AppConfig (platform.vendor_service_areas) as a
 * JSON array of entries. The entry shape is a discriminated union so
 * a future state-wide entry ({ type: 'state', code: 'CA' }) is an added member plus a
 * backend handler, not a rewrite. The SAME schema validates the admin
 * editor's PUT body on the backend, so a malformed area can never enter
 * config through the panel; the runtime gate additionally fails loud at
 * evaluation time, which covers rows written outside the API.
 */
import { z } from 'zod';
import { envelope, IsoDateTimeString, PointQuerySchema } from './_primitives';

export const SERVICE_AREA_ROUTES = {
  /** GET — current area list; PUT — replace it (UpdateServiceAreasRequestSchema) */
  adminAreas: 'admin/service-areas',
  /** GET — the caller's own latest request (or null); POST — create one. Authenticated user. */
  request: 'user/service-area-request',
  /** GET — is a store address inside the service area? params: ServiceAreaCheckQuerySchema. Authenticated user. */
  check: 'user/service-area-check',
  /** GET — admin review queue; params: GetServiceAreaRequestsQuerySchema. */
  adminRequests: 'admin/service-areas/requests',
  /** PUT — decide one request (DecideServiceAreaRequestSchema). */
  adminRequestDecision: (id: number) => `admin/service-areas/requests/${id}/decision`,
} as const;

export const RadiusServiceAreaSchema = z.object({
  type: z.literal('radius'),
  /** Human label — shown in admin and named in the vendor-facing rejection copy. */
  label: z.string().min(1, { error: 'Every service area needs a label' }),
  center: z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
  }),
  miles: z
    .number()
    .positive({ error: 'Radius must be a positive number of miles' }),
});

export const ServiceAreaSchema = z.discriminatedUnion('type', [
  RadiusServiceAreaSchema,
]);
export type ServiceArea = z.infer<typeof ServiceAreaSchema>;

export const VendorServiceAreasSchema = z.array(ServiceAreaSchema);
export type VendorServiceAreas = z.infer<typeof VendorServiceAreasSchema>;

/**
 * PUT body. An empty list is legal and means CLOSED (no vendor can
 * onboard anywhere) — the backend accepts it but the admin UI warns.
 */
export const UpdateServiceAreasRequestSchema = z.object({
  areas: VendorServiceAreasSchema,
});
export type UpdateServiceAreasRequest = z.infer<
  typeof UpdateServiceAreasRequestSchema
>;

/**
 * A county the areas reach, and whether the address search holds its
 * addresses (docs/plans/address-search.md, Decisions 8 and 17). A county
 * without them still takes vendors; its addresses go only to the Census
 * Bureau, which finds a whole address and suggests nothing as people type,
 * so the admin page warns.
 */
export const ServiceAreaCountySchema = z.object({
  /** "Los Angeles County". */
  county: z.string().min(1),
  /** The county's last address load, or null when its addresses are not loaded. */
  addresses: z
    .object({
      loadedAt: IsoDateTimeString,
      count: z.number().int().nonnegative(),
    })
    .nullable(),
});
export type ServiceAreaCounty = z.infer<typeof ServiceAreaCountySchema>;

export const ServiceAreasResultSchema = z.object({
  areas: VendorServiceAreasSchema,
  /** Every county the areas reach, by name. */
  counties: z.array(ServiceAreaCountySchema),
});
export const ServiceAreasResultEnvelopeSchema = envelope(
  ServiceAreasResultSchema,
);
export const unwrapServiceAreasResult = (
  response: z.infer<typeof ServiceAreasResultEnvelopeSchema>,
) => response.data;

// ─── Out-of-area onboarding requests ────────────────────────────────────
//
// A would-be vendor whose address falls outside the areas can request
// onboarding anyway. An APPROVED request row IS the per-account
// exemption the onboarding gate honors — there is no separate exemption
// flag to drift out of sync with the review decision.

export const ServiceAreaRequestStatusSchema = z.enum([
  'PENDING',
  'APPROVED',
  'DENIED',
]);
export type ServiceAreaRequestStatus = z.infer<
  typeof ServiceAreaRequestStatusSchema
>;

/** Free-text cap shared by the requester note and the denial reason. */
export const SERVICE_AREA_NOTE_MAX_LENGTH = 500;

/**
 * POST body — the geocoded address snapshot at request time plus an
 * optional note. Mirrors the become-vendor address fields; lat/lng come
 * from the same client geocode the form already performs.
 */
export const CreateServiceAreaRequestSchema = z.object({
  street: z.string().min(1),
  city: z.string().min(1),
  state: z.string().min(1),
  zipCode: z.string().min(1),
  country: z.string().min(1),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  note: z.string().max(SERVICE_AREA_NOTE_MAX_LENGTH).optional(),
});
export type CreateServiceAreaRequest = z.infer<
  typeof CreateServiceAreaRequestSchema
>;

/** One request row as both the owner and admins see it. */
export const ServiceAreaRequestSchema = z.object({
  id: z.number().int(),
  userId: z.string(),
  street: z.string(),
  city: z.string(),
  state: z.string(),
  zipCode: z.string(),
  country: z.string(),
  lat: z.number(),
  lng: z.number(),
  note: z.string().nullable(),
  status: ServiceAreaRequestStatusSchema,
  denialReason: z.string().nullable(),
  reviewedAt: IsoDateTimeString.nullable(),
  createdAt: IsoDateTimeString,
});
export type ServiceAreaRequestRow = z.infer<typeof ServiceAreaRequestSchema>;

/** GET user/service-area-request — the caller's latest request, if any. */
export const MyServiceAreaRequestResultSchema = z.object({
  request: ServiceAreaRequestSchema.nullable(),
});
export const MyServiceAreaRequestEnvelopeSchema = envelope(
  MyServiceAreaRequestResultSchema,
);
export const unwrapMyServiceAreaRequest = (
  response: z.infer<typeof MyServiceAreaRequestEnvelopeSchema>,
) => response.data;

export const CreateServiceAreaRequestResultSchema = z.object({
  request: ServiceAreaRequestSchema,
});
export const CreateServiceAreaRequestResultEnvelopeSchema = envelope(
  CreateServiceAreaRequestResultSchema,
);
export const unwrapCreateServiceAreaRequestResult = (
  response: z.infer<typeof CreateServiceAreaRequestResultEnvelopeSchema>,
) => response.data;

// ─── Admin review queue ─────────────────────────────────────────────────

export const GetServiceAreaRequestsQuerySchema = z.object({
  status: z.enum(['all', 'pending', 'approved', 'denied']).default('pending'),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type GetServiceAreaRequestsQuery = z.infer<
  typeof GetServiceAreaRequestsQuerySchema
>;

export const AdminServiceAreaRequestSchema = ServiceAreaRequestSchema.extend({
  user: z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
  }),
});
export const AdminServiceAreaRequestsListSchema = z.object({
  items: z.array(AdminServiceAreaRequestSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});
export const AdminServiceAreaRequestsListEnvelopeSchema = envelope(
  AdminServiceAreaRequestsListSchema,
);
export const unwrapAdminServiceAreaRequestsList = (
  response: z.infer<typeof AdminServiceAreaRequestsListEnvelopeSchema>,
) => response.data;

export const DecideServiceAreaRequestSchema = z.object({
  decision: z.enum(['APPROVED', 'DENIED']),
  denialReason: z.string().max(SERVICE_AREA_NOTE_MAX_LENGTH).optional(),
});
export type DecideServiceAreaRequest = z.infer<
  typeof DecideServiceAreaRequestSchema
>;

export const DecideServiceAreaRequestResultSchema = z.object({
  request: ServiceAreaRequestSchema,
});
export const DecideServiceAreaRequestResultEnvelopeSchema = envelope(
  DecideServiceAreaRequestResultSchema,
);
export const unwrapDecideServiceAreaRequestResult = (
  response: z.infer<typeof DecideServiceAreaRequestResultEnvelopeSchema>,
) => response.data;

/**
 * The wire marker on the 400 that refuses a store application whose pickup
 * location is outside the service area. It rides in the response's
 * `data.code`, and the become-a-vendor form branches on it to offer the
 * request-anyway flow instead of a dead end. Defined here once: until
 * 2026-09-21 the backend defined it and the web form retyped the string.
 */
export const OUT_OF_SERVICE_AREA_CODE = 'OUT_OF_SERVICE_AREA';

// ─── Pre-submit check (the become-a-vendor Location step, GG-504) ───────
//
// The become-vendor POST runs the geography gate itself. This read lets
// the stepper ask at the Location step, BEFORE the applicant fills in
// hours, photos, socials and business details, so an out-of-area
// address gets the request-anyway flow on the spot instead of at the
// very end. Same service function, same exemption rule (an APPROVED
// request for the caller passes), so the two answers can never differ.

export const ServiceAreaCheckQuerySchema = PointQuerySchema;
export type ServiceAreaCheckQuery = z.infer<typeof ServiceAreaCheckQuerySchema>;

export const ServiceAreaCheckResultSchema = z.object({
  allowed: z.boolean(),
  /** The vendor-facing rejection copy when `allowed` is false; null when allowed. */
  message: z.string().nullable(),
});
export type ServiceAreaCheckResult = z.infer<typeof ServiceAreaCheckResultSchema>;
export const ServiceAreaCheckEnvelopeSchema = envelope(ServiceAreaCheckResultSchema);
export const unwrapServiceAreaCheck = (
  response: z.infer<typeof ServiceAreaCheckEnvelopeSchema>,
) => response.data;
